/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AbstractHttpService } from '@src/app/shared/abstract-http.service';
import { Observable } from 'rxjs';
import { OmcSettings } from '@src/app/model/OmcSettings';
import { AbstractObject, uuid } from '@src/app/model/abstract-object';
import { MessageService } from '@src/app/shared/message.service';
import { catchError, tap } from 'rxjs/operators';
import { Agent } from '@src/app/model/Agent';

@Injectable({
    providedIn: 'root'
})
export class OmcSettingsService extends AbstractHttpService{
    /** If you are editing here, you likely need to make same change in AgentSettingsComponent */
    protected get baseUrl() {
        return this.getAjaxPath('omcSettings');
    }
    protected cache: OmcSettings[];
    protected typeString = 'OMCSettings';

    protected http = inject(HttpClient);
    protected messageService = inject(MessageService);

    getAgencies() : Observable<Agency[]> {
        return this.http.get<Agency[]>(this.baseUrl + '/agencies')
    }

    addAgent(agent: Partial<Agent>) : Observable<Agent> {
        const url = this.baseUrl + '/agent';
        return this.http.post(url, agent, this.getSimpleOptions(agent.teamId)).pipe(
            tap((x) => {
                if (x) {
                    this.messageService.show('Agent has been granted access to OMC');
                }
                return x;
            }),
            catchError(this.handleOneError('Adding Agent to OMC', null))
        );
    }

    /**
     * Hand the OMC over to a different one of its agencies.
     *
     * teams.primaryAgentId is what every agent managed write is stamped from, so this moves new
     * requests, actions and jobs - and the OMC's bank accounts with them. Support only, server side.
     */
    setPrimaryAgent(agentTeamId: uuid): Observable<OmcSettings> {
        return this.http.put<OmcSettings>(this.baseUrl + '/primaryAgent', { agentTeamId },
            this.getSimpleOptions(null)).pipe(
            tap(x => {
                if (x) {
                    this.messageService.show('Primary agent changed, bank accounts moved with it');
                }
            }),
            catchError(this.handleOneError('Changing Primary Agent', null))
        );
    }

    sendTestMail(omcSettings: OmcSettings) {
        this.http.post(this.baseUrl + '/sendTestMail', {}, this.getSimpleOptions(omcSettings.id)).subscribe(
            result => {
                console.log(result);
                this.messageService.show('Test mail sent, please check your mailbox');
            },
            error => {
                this.messageService.showError(error, 'Sending Test Mail');
            }
        )
    }

    deleteParameter(parmName: string, parmValue: string | uuid, existing: OmcSettings) : Observable<OmcSettings> {
        const opts = this.getSimpleOptions(null);
        opts['body'] = { currentValue: parmValue};
        return this.http.delete<OmcSettings>(this.baseUrl+ '/' + parmName, opts ).pipe(
            tap( (x) => {
                if (x) {
                    this.messageService.show('Parameter removed and reverted to default');
                }
            }),
            catchError(this.handleOneError('Removing OMC Setting', existing))
        )
    }
}

export class Agency extends AbstractObject {
    agentTeamId: uuid;
    agentRoleId: uuid;
    omcRoleId: uuid;
}
